import { ReactNode } from 'react'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { I18nShape } from '@tribeplatform/react-components/i18n'
import { type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'

import { SiteRouteSidebar } from '../Site/SiteRouteSidebar.js'
import { StoreSidebar } from '../Store/StoreSidebar.js'

export interface SidebarItem {
  title: ReactNode
  description?: ReactNode
  icon?: SvgIconName | JSX.Element
  path?: string
  href?: string
  exact?: boolean
  children?: Array<SidebarItem>
  component?: JSX.Element
  backTo?: string | false
  featureFlag?: string
  arrow?: boolean
  key?: string
  noPaddingX?: boolean
  noPaddingY?: boolean
  noNest?: boolean
  hidden?: boolean
  fullPage?: boolean
}

export const sidebarsFactory: (
  intl: I18nShape,
  eventContentModuleAccess: boolean,
) => Array<SidebarItem> = (intl, eventContentModuleAccess) => {
  const { $t } = intl
  return [
    {
      title: $t({ defaultMessage: 'People', id: 'Generics.People' }),
      icon: 'members',
      path: adminRoutes.people.members,
      children: [
        {
          title: $t({ defaultMessage: '{MEMBERS_CC}', id: 'Generics.Members' }),
          path: adminRoutes.people.members,
        },
        {
          title: $t({
            defaultMessage: 'Staff',
            id: 'Generics.Staff',
          }),
          path: adminRoutes.people.staff,
        },
        {
          title: $t({
            defaultMessage: 'Invitations',
            id: 'Generics.Invitations',
          }),
          path: adminRoutes.people.invitations,
        },
        {
          title: $t({
            defaultMessage: 'Profile fields',
            id: 'Generics.ProfileFields',
          }),
          path: adminRoutes.people.fields,
        },
        {
          title: $t({ defaultMessage: 'Badges', id: 'Generics.Badges' }),
          path: adminRoutes.people.badges,
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Content', id: 'Generics.Content' }),
      icon: 'content',
      path: adminRoutes.content.root,
      children: [
        {
          title: $t({ defaultMessage: 'Posts', id: 'Generics.Posts' }),
          path: adminRoutes.content.posts,
          exact: true,
        },
        ...(eventContentModuleAccess
          ? [
              {
                title: $t({ defaultMessage: 'Events', id: 'Generics.Events' }),
                path: adminRoutes.content.events,
                exact: true,
              },
            ]
          : []),
        {
          title: $t({ defaultMessage: '{SPACES_CC}', id: 'Generics.Spaces' }),
          path: adminRoutes.content.spaces,
          exact: true,
        },
        {
          title: $t({ defaultMessage: '{TAGS_CC}', id: 'Generics.Tags' }),
          path: adminRoutes.content.tags,
        },
      ],
    },
    {
      title: $t({
        defaultMessage: 'Site',
        id: 'Generics.Site',
      }),
      icon: 'monitor-03',
      path: adminRoutes.site.root,
      component: <SiteRouteSidebar />,
    },
    {
      title: $t({ defaultMessage: 'Settings', id: 'Generics.Settings' }),
      icon: 'settings',
      path: adminRoutes.settings.network.root,
      children: [
        {
          title: $t({
            defaultMessage: 'Site settings',
            id: 'Generics.CommunitySettings',
          }),
          path: adminRoutes.settings.network.root,
        },

        {
          title: $t({
            defaultMessage: 'Authentication',
            id: 'Generics.Authentication',
          }),
          path: adminRoutes.settings.authentication.root,
        },
        {
          title: $t({ defaultMessage: 'Domain', id: 'Generics.Domain' }),
          path: adminRoutes.settings.domain,
        },
        {
          title: $t({
            defaultMessage: 'Search',
            id: 'Generics.Search',
          }),
          path: adminRoutes.settings.search.root,
        },
        {
          title: $t({
            defaultMessage: 'Messaging',
            id: 'Generics.Messaging',
          }),
          path: adminRoutes.settings.messaging.root,
        },
        {
          title: $t({
            defaultMessage: 'Moderation',
            id: 'Generics.Moderation',
          }),
          path: adminRoutes.settings.moderation.root,
        },
        {
          title: $t({
            defaultMessage: 'Localization',
            id: 'Generics.Localization',
          }),
          path: adminRoutes.settings.localization.root,
        },
        {
          title: $t({
            defaultMessage: 'Notifications',
            id: 'Generics.Notifications',
          }),
          path: adminRoutes.settings.notifications.root,
        },
        {
          title: $t({
            defaultMessage: 'SEO settings',
            id: 'Generics.SeoSettings',
          }),
          path: adminRoutes.settings.seo.root,
        },
        {
          title: $t({
            defaultMessage: 'Security & Privacy',
            id: 'Generics.SecurityAndPrivacy',
          }),
          path: adminRoutes.settings.security.root,
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Billing', id: 'Generics.Billing' }),
      path: adminRoutes.billing.root,
      icon: 'credit-card',
      backTo: false,
      children: [
        {
          title: $t({
            defaultMessage: 'Summary',
            id: 'Generics.Summary',
          }),
          path: adminRoutes.billing.root,
          exact: true,
        },
        {
          title: $t({
            defaultMessage: 'Subscription plans',
            id: 'Generics.SubscriptionPlans',
          }),
          path: adminRoutes.billing.plans,
        },
        {
          title: $t({
            defaultMessage: 'Service usage',
            id: 'Generics.ServiceUsage',
          }),
          path: adminRoutes.billing.usages,
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Reports', id: 'Generics.Reports' }),
      icon: 'analytics',
      path: adminRoutes.reports.analytics.root,
      key: `${adminRoutes.reports.analytics.root}#root`,
      backTo: false,
      children: [
        {
          title: $t({ defaultMessage: 'Overview', id: 'Generics.Overview' }),
          key: adminRoutes.reports.analytics.root,
          path: adminRoutes.reports.analytics.root,
          exact: true,
          arrow: false,
        },
        {
          title: $t({
            defaultMessage: 'Reach & engagement',
            id: 'Generics.ReachAndEngagement',
          }),
          path: adminRoutes.reports.analytics.reach,
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'People', id: 'Generics.People' }),
          path: adminRoutes.reports.analytics.people,
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'Posts', id: 'Generics.Posts' }),
          path: adminRoutes.reports.analytics.posts,
          arrow: false,
        },
        ...(eventContentModuleAccess
          ? [
              {
                title: $t({ defaultMessage: 'Events', id: 'Generics.Events' }),
                path: adminRoutes.reports.analytics.events,
                arrow: false,
              },
            ]
          : []),
        {
          title: $t({ defaultMessage: '{SPACES_CC}', id: 'Generics.Spaces' }),
          path: adminRoutes.reports.analytics.spaces,
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'Messages', id: 'Generics.Messages' }),
          path: adminRoutes.reports.analytics.messages,
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'Audit logs', id: 'Generics.AuditLogs' }),
          path: adminRoutes.reports.auditLogs,
          key: `${adminRoutes.reports.auditLogs}#AuditLogs`,
        },
        {
          title: $t({ defaultMessage: 'Email logs', id: 'Generics.EmailLogs' }),
          path: adminRoutes.reports.emailLogs,
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'App store', id: 'Generics.AppStore' }),
      icon: 'grid',
      path: adminRoutes.appStore.root,
      href: adminRoutes.appStore.integrations,
      component: <StoreSidebar />,
    },
    {
      title: $t({ defaultMessage: 'Onboarding', id: 'Generics.Onboarding' }),
      icon: 'graduation-hat-01',
      path: adminRoutes.onboarding,
    },
  ]
}

export const virtualSidebarRootFactory: (
  intl: I18nShape,
  eventContentModuleAccess: boolean,
) => SidebarItem = (intl, eventContentModuleAccess) => ({
  title: '/',
  icon: undefined,
  path: adminRoutes.root,
  children: sidebarsFactory(intl, eventContentModuleAccess),
})
